/**
 * Database Module
 *
 * Uses SQLocal for SQLite in the browser via OPFS.
 */

import { SQLocal } from 'sqlocal';

// Database configuration
const DATABASE_PATH = 'kokuromotie.sqlite3';
const BROADCAST_CHANNEL = 'kokuromotie-db-sync';

// Create SQLocal instance
const db = new SQLocal(DATABASE_PATH);

// Get the sql tagged template function
const { sql } = db;

console.log('[Database] SQLocal instance created for:', DATABASE_PATH);

// Export sql for direct queries
export { sql };

// Create broadcast channel for cross-tab coordination
const channel = new BroadcastChannel(BROADCAST_CHANNEL);

// Track if database is ready
let isReady = false;
let readyResolve;
let readyReject;

export const dbReady = new Promise((resolve, reject) => {
  readyResolve = resolve;
  readyReject = reject;
});

// Database change listeners
const changeListeners = new Set();

export function onDatabaseChange(listener) {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

channel.onmessage = (event) => {
  const { type, payload } = event.data;
  if (type === 'DB_CHANGE') {
    for (const listener of changeListeners) {
      try {
        listener(payload);
      } catch (e) {
        console.error('[Database] Change listener error:', e);
      }
    }
  }
};

function broadcastChange(table, action, id = null) {
  channel.postMessage({
    type: 'DB_CHANGE',
    payload: { table, action, id, timestamp: Date.now() }
  });

  for (const listener of changeListeners) {
    try {
      listener({ table, action, id, timestamp: Date.now(), local: true });
    } catch (e) {
      console.error('[Database] Change listener error:', e);
    }
  }
}

// ============================================================================
// Database Initialization
// ============================================================================

export async function initSchema() {
  try {
    console.log('[Database] Initializing schema...');

    // Test connection
    const testResult = await sql`SELECT sqlite_version() as version`;
    console.log('[Database] SQLite version:', testResult[0]?.version);

    // Create locations table
    console.log('[Database] Creating locations table...');
    await sql`
      CREATE TABLE IF NOT EXISTS locations (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT NOT NULL,
        longitude REAL NOT NULL,
        latitude REAL NOT NULL,
        description TEXT,
        category TEXT DEFAULT 'default',
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        synced INTEGER DEFAULT 0
      )
    `;

    // Verify table exists
    const tablesAfterLocations = await sql`SELECT name FROM sqlite_master WHERE type='table' AND name='locations'`;
    console.log('[Database] Locations table exists:', tablesAfterLocations.length > 0);

    // Create sync_log table
    console.log('[Database] Creating sync_log table...');
    await sql`
      CREATE TABLE IF NOT EXISTS sync_log (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        table_name TEXT NOT NULL,
        record_id INTEGER NOT NULL,
        action TEXT NOT NULL,
        timestamp TEXT DEFAULT CURRENT_TIMESTAMP,
        synced INTEGER DEFAULT 0
      )
    `;

    // Create gps_trails table (recorded GPS tracks)
    console.log('[Database] Creating gps_trails table...');
    await sql`
      CREATE TABLE IF NOT EXISTS gps_trails (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        client_uuid TEXT UNIQUE,
        name TEXT,
        started_at TEXT NOT NULL,
        ended_at TEXT,
        status TEXT NOT NULL DEFAULT 'recording',
        point_count INTEGER NOT NULL DEFAULT 0,
        distance_m REAL NOT NULL DEFAULT 0,
        synced INTEGER NOT NULL DEFAULT 0,
        remote_id TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )
    `;

    // Create gps_trail_points table (individual recorded fixes)
    console.log('[Database] Creating gps_trail_points table...');
    await sql`
      CREATE TABLE IF NOT EXISTS gps_trail_points (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        trail_id INTEGER NOT NULL,
        seq INTEGER NOT NULL,
        longitude REAL NOT NULL,
        latitude REAL NOT NULL,
        altitude REAL,
        accuracy REAL,
        altitude_accuracy REAL,
        heading REAL,
        speed REAL,
        satellites INTEGER,
        recorded_at TEXT NOT NULL
      )
    `;

    // Create app_settings table (key/value app settings incl. consents).
    console.log('[Database] Creating app_settings table...');
    await sql`
      CREATE TABLE IF NOT EXISTS app_settings (
        key TEXT PRIMARY KEY,
        value TEXT,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      )
    `;

    // Create location_fixes table (ambient presence tracking — low-frequency,
    // distance-filtered fixes used to surface nearby emergency alerts and
    // business recommendations). Distinct from gps_trail_points, which is the
    // user-initiated high-fidelity trail feature.
    console.log('[Database] Creating location_fixes table...');
    await sql`
      CREATE TABLE IF NOT EXISTS location_fixes (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        client_uuid TEXT UNIQUE,
        longitude REAL NOT NULL,
        latitude REAL NOT NULL,
        accuracy REAL,
        altitude REAL,
        heading REAL,
        speed REAL,
        mode TEXT,
        recorded_at TEXT NOT NULL,
        synced INTEGER NOT NULL DEFAULT 0,
        remote_id TEXT
      )
    `;

    // Create location_attributes table (extensible key/value per location).
    // Lets user-type-specific fields (e.g. a restaurant's menu specials) be
    // stored without schema changes as new business types are onboarded.
    console.log('[Database] Creating location_attributes table...');
    await sql`
      CREATE TABLE IF NOT EXISTS location_attributes (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        location_id INTEGER NOT NULL,
        key TEXT NOT NULL,
        value TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        UNIQUE(location_id, key)
      )
    `;

    // Create location_media table. The media file itself lives in OPFS
    // (see src/media/mediaStore.js); this row holds the reference + metadata.
    console.log('[Database] Creating location_media table...');
    await sql`
      CREATE TABLE IF NOT EXISTS location_media (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        media_uuid TEXT UNIQUE NOT NULL,
        location_id INTEGER NOT NULL,
        kind TEXT NOT NULL,            -- 'photo' | 'video'
        filename TEXT NOT NULL,        -- OPFS path under media/
        mime TEXT,
        size INTEGER,
        duration_ms INTEGER,           -- videos only
        synced INTEGER NOT NULL DEFAULT 0,
        remote_id TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )
    `;

    // Create gps_trail_meta table (form-captured attributes per trail).
    // Separate from gps_trails so the attribute set can grow over time without
    // migrating the core trail table. meta_uuid is a stable unique id linking a
    // metadata record to its trail (trail_id) — used to know who entered which
    // track.
    console.log('[Database] Creating gps_trail_meta table...');
    await sql`
      CREATE TABLE IF NOT EXISTS gps_trail_meta (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        meta_uuid TEXT UNIQUE NOT NULL,
        trail_id INTEGER NOT NULL,
        tracked_by TEXT,
        local_area TEXT,
        local_name TEXT,
        surface TEXT,
        tracked_at TEXT NOT NULL,
        synced INTEGER NOT NULL DEFAULT 0,
        remote_id TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )
    `;

    // Create indexes
    await sql`CREATE INDEX IF NOT EXISTS idx_locations_category ON locations(category)`;
    await sql`CREATE INDEX IF NOT EXISTS idx_locations_synced ON locations(synced)`;
    await sql`CREATE INDEX IF NOT EXISTS idx_gps_trails_synced ON gps_trails(synced, status)`;
    await sql`CREATE INDEX IF NOT EXISTS idx_gps_trail_points_trail ON gps_trail_points(trail_id, seq)`;
    await sql`CREATE INDEX IF NOT EXISTS idx_gps_trail_meta_trail ON gps_trail_meta(trail_id)`;
    await sql`CREATE INDEX IF NOT EXISTS idx_location_fixes_synced ON location_fixes(synced, recorded_at)`;
    await sql`CREATE INDEX IF NOT EXISTS idx_location_attributes_loc ON location_attributes(location_id)`;
    await sql`CREATE INDEX IF NOT EXISTS idx_location_media_loc ON location_media(location_id)`;

    // Final verification
    const allTables = await sql`SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'`;
    console.log('[Database] All tables:', allTables.map(t => t.name));

    isReady = true;
    readyResolve(true);
    console.log('[Database] ✓ Schema initialized');

  } catch (error) {
    console.error('[Database] ✗ Schema init failed:', error);
    readyReject(error);
    throw error;
  }
}

// ============================================================================
// Location Operations
// ============================================================================

export async function addLocation(name, longitude, latitude, options = {}) {
  const { description = null, category = 'default' } = options;

  console.log('[Database] Adding location:', name, longitude, latitude, category);

  try {
    // Check table exists first
    const tableCheck = await sql`SELECT name FROM sqlite_master WHERE type='table' AND name='locations'`;
    console.log('[Database] Table check before insert:', tableCheck);

    if (tableCheck.length === 0) {
      console.error('[Database] ✗ locations table does not exist!');
      throw new Error('locations table does not exist');
    }

    // Insert - using explicit values
    console.log('[Database] Executing INSERT...');
    await sql`
      INSERT INTO locations (name, longitude, latitude, description, category)
      VALUES (${name}, ${longitude}, ${latitude}, ${description}, ${category})
    `;
    console.log('[Database] INSERT completed');

    // Get the ID
    const idResult = await sql`SELECT last_insert_rowid() as id`;
    const newId = idResult[0]?.id;
    console.log('[Database] New ID:', newId);

    // Verify it was actually inserted
    const verifyResult = await sql`SELECT * FROM locations WHERE id = ${newId}`;
    console.log('[Database] Verify insert:', verifyResult);

    if (verifyResult.length === 0) {
      console.error('[Database] ✗ Insert verification failed - row not found!');
      throw new Error('Insert verification failed');
    }

    // Log for sync
    await sql`
      INSERT INTO sync_log (table_name, record_id, action)
      VALUES ('locations', ${newId}, 'INSERT')
    `;

    broadcastChange('locations', 'INSERT', newId);

    console.log('[Database] ✓ Location added:', newId);
    return { id: newId };

  } catch (error) {
    console.error('[Database] ✗ Failed to add location:', error);
    throw error;
  }
}

export async function getLocations(options = {}) {
  const { category = null, limit = 1000 } = options;

  try {
    // First check if table exists
    const tableCheck = await sql`SELECT name FROM sqlite_master WHERE type='table' AND name='locations'`;
    console.log('[Database] getLocations - table exists:', tableCheck.length > 0);

    if (tableCheck.length === 0) {
      console.warn('[Database] locations table does not exist yet');
      return [];
    }

    let results;
    if (category) {
      results = await sql`
        SELECT * FROM locations
        WHERE category = ${category}
        ORDER BY created_at DESC
        LIMIT ${limit}
      `;
    } else {
      results = await sql`
        SELECT * FROM locations
        ORDER BY created_at DESC
        LIMIT ${limit}
      `;
    }

    console.log('[Database] getLocations returned', results.length, 'rows');
    return results;

  } catch (error) {
    console.error('[Database] getLocations error:', error);
    return [];
  }
}

export async function getLocation(id) {
  try {
    const results = await sql`SELECT * FROM locations WHERE id = ${id}`;
    return results[0] || null;
  } catch (error) {
    console.error('[Database] getLocation error:', error);
    return null;
  }
}

export async function updateLocation(id, updates) {
  const { name, longitude, latitude, description, category } = updates;

  try {
    const location = await getLocation(id);
    if (!location) {
      throw new Error(`Location ${id} not found`);
    }

    await sql`
      UPDATE locations
      SET
        name = ${name ?? location.name},
        longitude = ${longitude ?? location.longitude},
        latitude = ${latitude ?? location.latitude},
        description = ${description ?? location.description},
        category = ${category ?? location.category},
        updated_at = CURRENT_TIMESTAMP,
        synced = 0
      WHERE id = ${id}
    `;

    await sql`
      INSERT INTO sync_log (table_name, record_id, action)
      VALUES ('locations', ${id}, 'UPDATE')
    `;

    broadcastChange('locations', 'UPDATE', id);
    console.log('[Database] ✓ Location updated:', id);

  } catch (error) {
    console.error('[Database] ✗ updateLocation error:', error);
    throw error;
  }
}

export async function deleteLocation(id) {
  try {
    await sql`
      INSERT INTO sync_log (table_name, record_id, action)
      VALUES ('locations', ${id}, 'DELETE')
    `;

    await sql`DELETE FROM locations WHERE id = ${id}`;

    broadcastChange('locations', 'DELETE', id);
    console.log('[Database] ✓ Location deleted:', id);

  } catch (error) {
    console.error('[Database] ✗ deleteLocation error:', error);
    throw error;
  }
}

export async function getLocationCount() {
  try {
    const result = await sql`SELECT COUNT(*) as count FROM locations`;
    return result[0]?.count ?? 0;
  } catch (error) {
    console.error('[Database] getLocationCount error:', error);
    return 0;
  }
}

// ============================================================================
// Sync Operations
// ============================================================================

export async function getUnsyncedChanges() {
  return sql`SELECT * FROM sync_log WHERE synced = 0 ORDER BY timestamp ASC`;
}

export async function markSynced(syncLogIds) {
  if (!syncLogIds.length) return;
  for (const id of syncLogIds) {
    await sql`UPDATE sync_log SET synced = 1 WHERE id = ${id}`;
  }
}

export async function getUnsyncedLocations() {
  return sql`SELECT * FROM locations WHERE synced = 0`;
}

export async function markLocationsSynced(ids) {
  if (!ids.length) return;
  for (const id of ids) {
    await sql`UPDATE locations SET synced = 1 WHERE id = ${id}`;
  }
}

// ============================================================================
// GPS Trail Operations
//
// Storage backing for the reusable GeoTracker engine (src/geotracker/). These
// functions are the SQLocal storage adapter surface used by
// src/geotracker-bisawofa.js. To reuse the GPS module in another app, lift the
// two tables (see initSchema) and these helpers alongside src/geotracker/.
// ============================================================================

/**
 * Create a new trail row (status='recording') and return its local id.
 * @param {object} meta { uuid, name, startedAt }
 * @returns {Promise<number>} local trail id
 */
export async function createGpsTrail(meta) {
  const { uuid, name = null, startedAt } = meta;
  await sql`
    INSERT INTO gps_trails (client_uuid, name, started_at, status)
    VALUES (${uuid}, ${name}, ${startedAt}, 'recording')
  `;
  const idResult = await sql`SELECT last_insert_rowid() as id`;
  const id = idResult[0]?.id;
  broadcastChange('gps_trails', 'INSERT', id);
  return id;
}

/**
 * Append one recorded point to a trail.
 * @param {number} trailId
 * @param {object} point normalized fix + { seq }
 */
export async function addGpsTrailPoint(trailId, point) {
  const {
    seq, lon, lat,
    altitude = null, accuracy = null, altitudeAccuracy = null,
    heading = null, speed = null, satellites = null, timestamp,
  } = point;
  const recordedAt = typeof timestamp === 'number'
    ? new Date(timestamp).toISOString()
    : (timestamp || new Date().toISOString());
  await sql`
    INSERT INTO gps_trail_points
      (trail_id, seq, longitude, latitude, altitude, accuracy, altitude_accuracy, heading, speed, satellites, recorded_at)
    VALUES
      (${trailId}, ${seq}, ${lon}, ${lat}, ${altitude}, ${accuracy}, ${altitudeAccuracy}, ${heading}, ${speed}, ${satellites}, ${recordedAt})
  `;
}

/**
 * Finalise a trail: mark completed and store the summary.
 * @param {number} trailId
 * @param {object} summary { endedAt, pointCount, distanceM }
 */
export async function finishGpsTrail(trailId, summary) {
  const { endedAt, pointCount = 0, distanceM = 0 } = summary;
  await sql`
    UPDATE gps_trails
       SET ended_at = ${endedAt}, point_count = ${pointCount}, distance_m = ${distanceM}, status = 'completed'
     WHERE id = ${trailId}
  `;
  broadcastChange('gps_trails', 'UPDATE', trailId);
}

/**
 * Trails that are completed but not yet pushed to a server.
 * @returns {Promise<Array>}
 */
export async function getUnsyncedGpsTrails() {
  return sql`SELECT * FROM gps_trails WHERE synced = 0 AND status = 'completed' ORDER BY started_at ASC`;
}

/**
 * All points of a trail, in recorded order.
 * @param {number} trailId
 * @returns {Promise<Array>}
 */
export async function getGpsTrailPoints(trailId) {
  return sql`SELECT * FROM gps_trail_points WHERE trail_id = ${trailId} ORDER BY seq ASC`;
}

/**
 * Mark a trail as synced and record the server-assigned id.
 * @param {number} trailId
 * @param {string|number|null} remoteId
 */
export async function markGpsTrailSynced(trailId, remoteId = null) {
  await sql`UPDATE gps_trails SET synced = 1, remote_id = ${remoteId} WHERE id = ${trailId}`;
  broadcastChange('gps_trails', 'UPDATE', trailId);
}

/**
 * List trails (most recent first) for a history/list UI.
 * @returns {Promise<Array>}
 */
export async function getGpsTrails() {
  return sql`SELECT * FROM gps_trails ORDER BY started_at DESC`;
}

/**
 * Delete a trail and all its points (and any captured metadata).
 * @param {number} trailId
 */
export async function deleteGpsTrail(trailId) {
  await sql`DELETE FROM gps_trail_points WHERE trail_id = ${trailId}`;
  await sql`DELETE FROM gps_trail_meta WHERE trail_id = ${trailId}`;
  await sql`DELETE FROM gps_trails WHERE id = ${trailId}`;
  broadcastChange('gps_trails', 'DELETE', trailId);
}

/**
 * Save the form-captured metadata for a trail. Generates a stable unique id
 * (meta_uuid) and links it to the trail via trail_id, so we can later tell who
 * entered which track. The attribute set is intentionally open — new columns
 * can be added to gps_trail_meta without touching the core trail tables.
 *
 * @param {object} meta
 * @param {number} meta.trailId      local gps_trails.id this record describes
 * @param {string} [meta.trackedBy]  who recorded the track
 * @param {string} [meta.localArea]  administrative / local area
 * @param {string} [meta.localName]  local name of the area
 * @param {string} [meta.surface]    surface of the track
 * @param {string} [meta.trackedAt]  ISO timestamp (generated by BisaWofa)
 * @returns {Promise<{ id:number, metaUuid:string }>}
 */
export async function saveGpsTrailMeta(meta) {
  const {
    trailId,
    trackedBy = null,
    localArea = null,
    localName = null,
    surface = null,
    trackedAt = new Date().toISOString(),
  } = meta;

  const metaUuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

  await sql`
    INSERT INTO gps_trail_meta
      (meta_uuid, trail_id, tracked_by, local_area, local_name, surface, tracked_at)
    VALUES
      (${metaUuid}, ${trailId}, ${trackedBy}, ${localArea}, ${localName}, ${surface}, ${trackedAt})
  `;
  const idResult = await sql`SELECT last_insert_rowid() as id`;
  const id = idResult[0]?.id;
  broadcastChange('gps_trail_meta', 'INSERT', id);
  return { id, metaUuid };
}

/**
 * Fetch the metadata record(s) captured for a trail, most recent first.
 * @param {number} trailId
 * @returns {Promise<Array>}
 */
export async function getGpsTrailMeta(trailId) {
  return sql`SELECT * FROM gps_trail_meta WHERE trail_id = ${trailId} ORDER BY created_at DESC`;
}

// ============================================================================
// App Settings (key/value, incl. consents)
// ============================================================================

/**
 * Read a setting. Returns `fallback` if the key is not present.
 * @param {string} key
 * @param {*} [fallback=null]
 * @returns {Promise<string|null>}
 */
export async function getSetting(key, fallback = null) {
  const rows = await sql`SELECT value FROM app_settings WHERE key = ${key}`;
  return rows.length ? rows[0].value : fallback;
}

/**
 * Write a setting (upsert).
 * @param {string} key
 * @param {string} value
 */
export async function setSetting(key, value) {
  await sql`
    INSERT INTO app_settings (key, value, updated_at)
    VALUES (${key}, ${value}, ${new Date().toISOString()})
    ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at
  `;
  broadcastChange('app_settings', 'UPSERT', null);
}

// ============================================================================
// User profile (type/role) — single source the future login will populate
// ============================================================================

const USER_TYPE_KEY = 'user_type';

/**
 * Current user type id (e.g. 'visitor', 'restaurant'). Falls back to 'visitor'.
 * @returns {Promise<string>}
 */
export async function getUserType() {
  return (await getSetting(USER_TYPE_KEY)) || 'visitor';
}

/**
 * Set the current user type. When login is implemented it writes this same
 * setting (plus, later, a server-verified role) — no other code changes.
 * @param {string} typeId
 */
export async function setUserType(typeId) {
  await setSetting(USER_TYPE_KEY, typeId);
}

// ============================================================================
// Location attributes (extensible key/value per location)
// ============================================================================

/**
 * Upsert a map of attributes against a location. Empty/blank values are
 * skipped so only meaningful entries are stored.
 * @param {number} locationId
 * @param {Record<string,string>} attrs
 */
export async function saveLocationAttributes(locationId, attrs) {
  if (!attrs) return;
  for (const [key, value] of Object.entries(attrs)) {
    if (value == null || String(value).trim() === '') continue;
    await sql`
      INSERT INTO location_attributes (location_id, key, value)
      VALUES (${locationId}, ${key}, ${String(value)})
      ON CONFLICT(location_id, key) DO UPDATE SET value = excluded.value
    `;
  }
  broadcastChange('location_attributes', 'UPSERT', locationId);
}

/**
 * Read a location's attributes as a plain object.
 * @param {number} locationId
 * @returns {Promise<Record<string,string>>}
 */
export async function getLocationAttributes(locationId) {
  const rows = await sql`SELECT key, value FROM location_attributes WHERE location_id = ${locationId}`;
  const out = {};
  for (const r of rows) out[r.key] = r.value;
  return out;
}

// ============================================================================
// Location media (photo / short video — file in OPFS, metadata here)
// ============================================================================

/**
 * Record a media file attached to a location. The file bytes are stored in
 * OPFS by src/media/mediaStore.js; this row references it.
 * @param {object} media { locationId, kind, filename, mime, size, durationMs }
 * @returns {Promise<{ id:number, mediaUuid:string }>}
 */
export async function addLocationMedia(media) {
  const {
    locationId, kind, filename, mime = null, size = null, durationMs = null,
  } = media;
  const mediaUuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
  await sql`
    INSERT INTO location_media (media_uuid, location_id, kind, filename, mime, size, duration_ms)
    VALUES (${mediaUuid}, ${locationId}, ${kind}, ${filename}, ${mime}, ${size}, ${durationMs})
  `;
  const idResult = await sql`SELECT last_insert_rowid() as id`;
  const id = idResult[0]?.id;
  broadcastChange('location_media', 'INSERT', id);
  return { id, mediaUuid };
}

/**
 * Media rows for a location, newest first.
 * @param {number} locationId
 * @returns {Promise<Array>}
 */
export async function getLocationMedia(locationId) {
  return sql`SELECT * FROM location_media WHERE location_id = ${locationId} ORDER BY created_at DESC`;
}

/**
 * Media not yet uploaded to the server (for a future media sync).
 * @param {number} [limit=20]
 * @returns {Promise<Array>}
 */
export async function getUnsyncedLocationMedia(limit = 20) {
  return sql`SELECT * FROM location_media WHERE synced = 0 ORDER BY created_at ASC LIMIT ${limit}`;
}

// ============================================================================
// Location Fixes (ambient presence tracking)
//
// Storage backing for the PresenceTracker (src/presence/). Low-frequency,
// distance-filtered fixes; synced to the server in batches.
// ============================================================================

/**
 * Insert one presence fix.
 * @param {object} fix { clientUuid, lon, lat, accuracy, altitude, heading, speed, mode, recordedAt }
 * @returns {Promise<number>} local id
 */
export async function addLocationFix(fix) {
  const {
    clientUuid,
    lon, lat,
    accuracy = null, altitude = null, heading = null, speed = null,
    mode = null,
    recordedAt = new Date().toISOString(),
  } = fix;
  await sql`
    INSERT INTO location_fixes
      (client_uuid, longitude, latitude, accuracy, altitude, heading, speed, mode, recorded_at)
    VALUES
      (${clientUuid}, ${lon}, ${lat}, ${accuracy}, ${altitude}, ${heading}, ${speed}, ${mode}, ${recordedAt})
  `;
  const idResult = await sql`SELECT last_insert_rowid() as id`;
  const id = idResult[0]?.id;
  broadcastChange('location_fixes', 'INSERT', id);
  return id;
}

/**
 * Fixes not yet pushed to the server, oldest first.
 * @param {number} [limit=200]
 * @returns {Promise<Array>}
 */
export async function getUnsyncedLocationFixes(limit = 200) {
  return sql`
    SELECT * FROM location_fixes WHERE synced = 0
    ORDER BY recorded_at ASC LIMIT ${limit}
  `;
}

/**
 * Mark a batch of fixes as synced.
 * @param {number[]} ids local ids
 */
export async function markLocationFixesSynced(ids) {
  if (!ids || !ids.length) return;
  for (const id of ids) {
    await sql`UPDATE location_fixes SET synced = 1 WHERE id = ${id}`;
  }
  broadcastChange('location_fixes', 'UPDATE', null);
}

/**
 * The most recent fix (for "where is the user now" queries).
 * @returns {Promise<object|null>}
 */
export async function getLatestLocationFix() {
  const rows = await sql`SELECT * FROM location_fixes ORDER BY recorded_at DESC LIMIT 1`;
  return rows.length ? rows[0] : null;
}

/**
 * Count stored fixes (for diagnostics / storage awareness).
 * @returns {Promise<number>}
 */
export async function getLocationFixCount() {
  const rows = await sql`SELECT COUNT(*) as n FROM location_fixes`;
  return rows[0]?.n ?? 0;
}

/**
 * Prune old, already-synced fixes to cap local storage. Keeps the last
 * `keepDays` days of history; unsynced fixes are never pruned.
 * @param {number} [keepDays=30]
 * @returns {Promise<void>}
 */
export async function pruneOldLocationFixes(keepDays = 30) {
  const cutoff = new Date(Date.now() - keepDays * 24 * 60 * 60 * 1000).toISOString();
  await sql`DELETE FROM location_fixes WHERE synced = 1 AND recorded_at < ${cutoff}`;
}

// ============================================================================
// Export / Import
// ============================================================================

export async function exportDatabase() {
  return db.getDatabaseFile();
}

export async function importDatabase(data) {
  await db.overwriteDatabaseFile(data);
  broadcastChange('*', 'IMPORT', null);
}

export async function downloadDatabase(filename = 'bisawofa-backup.sqlite3') {
  const data = await exportDatabase();
  const blob = new Blob([data], { type: 'application/x-sqlite3' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}

// Export to GeoJSON
export async function exportToGeoJSON() {
  const locations = await getLocations();

  return {
    type: 'FeatureCollection',
    features: locations.map((loc) => ({
      type: 'Feature',
      properties: {
        id: loc.id,
        name: loc.name,
        category: loc.category,
        notes: loc.notes,
        created_at: loc.created_at,
      },
      geometry: {
        type: 'Point',
        coordinates: [loc.lon, loc.lat],
      },
    })),
  };
}

// ============================================================================
// Utility & Debug
// ============================================================================

export async function getDatabaseStatus() {
  try {
    const tables = await sql`
      SELECT name FROM sqlite_master
      WHERE type='table' AND name NOT LIKE 'sqlite_%'
      ORDER BY name
    `;

    const locationCount = await getLocationCount();

    return {
      ready: isReady,
      databasePath: DATABASE_PATH,
      tables: tables.map(t => t.name),
      locationCount
    };
  } catch (error) {
    return {
      ready: false,
      error: error.message
    };
  }
}

// Debug function - call from console to test
export async function testDatabase() {
  console.log('=== DATABASE TEST ===');

  try {
    // 1. Check connection
    const version = await sql`SELECT sqlite_version() as v`;
    console.log('1. SQLite version:', version[0].v);

    // 2. Check tables
    const tables = await sql`SELECT name FROM sqlite_master WHERE type='table'`;
    console.log('2. Tables:', tables.map(t => t.name));

    // 3. Try to insert a test row
    console.log('3. Inserting test row...');
    await sql`INSERT INTO locations (name, longitude, latitude, category) VALUES ('TEST', -1.0, 7.0, 'test')`;

    // 4. Read it back
    const rows = await sql`SELECT * FROM locations WHERE name = 'TEST'`;
    console.log('4. Test row:', rows);

    // 5. Count all rows
    const count = await sql`SELECT COUNT(*) as c FROM locations`;
    console.log('5. Total rows:', count[0].c);

    // 6. Delete test row
    await sql`DELETE FROM locations WHERE name = 'TEST'`;
    console.log('6. Test row deleted');

    console.log('=== TEST PASSED ===');
    return true;
  } catch (error) {
    console.error('=== TEST FAILED ===', error);
    return false;
  }
}

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.testDatabase = testDatabase;
  window.dbStatus = getDatabaseStatus;
}

export async function closeDatabase() {
  channel.close();
  if (db.destroy) {
    await db.destroy();
  }
}

export default {
  sql,
  dbReady,
  initSchema,
  addLocation,
  getLocations,
  getLocation,
  updateLocation,
  deleteLocation,
  getLocationCount,
  getUnsyncedChanges,
  getUnsyncedLocations,
  markSynced,
  markLocationsSynced,
  exportDatabase,
  importDatabase,
  downloadDatabase,
  getDatabaseStatus,
  testDatabase,
  onDatabaseChange,
  closeDatabase
};
